export interface ConversationItem {
  id: string;
  channel: string;
  /** A public comment on one of the account's posts. */
  kind?: 'chat' | 'comment' | 'review' | 'call';
  /** Started from an ad. */
  fromAd?: boolean;
  /** The AI assistant is answering it. */
  assistantActive?: boolean;
  /** Reviews: stars given (1 to 5). */
  rating?: number | null;
  status: 'open' | 'pending' | 'snoozed' | 'closed';
  lastMessageAt: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  teamId: string | null;
  teamName: string | null;
  teamNameAr: string | null;
  firstResponseDueAt: string | null;
  firstResponseAt: string | null;
  slaBreachedAt: string | null;
  personId: string;
  personDisplayName: string;
  lastMessagePreview: string | null;
  lastDirection: 'inbound' | 'outbound' | null;
}

export interface ReplyPolicy {
  mode: 'free' | 'template' | 'unavailable';
  reason:
    'window_closed' | 'no_account' | 'channel_not_supported_yet' | 'no_reply_api' | 'no_recipient' | null;
  windowClosesAt: string | null;
  replyPhase?: number;
}

export interface Template {
  id: string;
  name: string;
  language: string;
  body: string;
  variables: number;
}

export interface ConversationDetail {
  /** The ad or campaign this conversation started from. */
  origin?: {
    campaignId: string | null;
    campaignName: string | null;
    platform: string | null;
    kind: 'broadcast' | 'paid' | null;
    adRef: { adId: string | null; headline: string | null; sourceType: string | null } | null;
  } | null;
  conversation: {
    id: string;
    channel: string;
    status: ConversationItem['status'];
    assigneeUserId: string | null;
    assigneeName: string | null;
    team: { id: string; name: string; nameAr: string | null } | null;
    requiredSkill: string | null;
    firstResponseDueAt: string | null;
    firstResponseAt: string | null;
    slaBreachedAt: string | null;
    lastInboundAt: string | null;
    kind?: ConversationItem['kind'];
    /** Comments and reviews: the post (text, link) or the stars. */
    context?: {
      postText?: string;
      postUrl?: string;
      postId?: string;
      rating?: number | null;
      reviewId?: string;
      source?: string;
    };
    /** Meta comments can also be answered with a private message. */
    canReplyPrivately?: boolean;
    assistantActive?: boolean;
    assistantHandedOffAt?: string | null;
  };
  person: { id: string; displayName: string };
  /** LY Voice is connected and the customer has a phone number. */
  canCall?: boolean;
  replyPolicy: ReplyPolicy;
  recipient: { type: string; value: string } | null;
  templates: Template[];
}

export interface ThreadItem {
  id: string;
  kind: 'message' | 'note';
  direction: 'inbound' | 'outbound' | 'internal';
  channel: string | null;
  text: string | null;
  sentAt: string;
  traceId: string | null;
  deliveryStatus: 'queued' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed' | 'unknown' | null;
  deliveryError: string | null;
  templateName: string | null;
  authorName: string | null;
  /** Written by the AI assistant. */
  byAssistant?: boolean;
  /** Replies to comments and reviews: on the post, or privately. */
  replyMode?: 'public' | 'private' | null;
  /** A phone call (LY Voice). */
  call?: CallInfo | null;
}

export interface CallInfo {
  id: string;
  direction: 'inbound' | 'outbound' | 'web';
  status: 'answered' | 'missed' | 'voicemail' | 'failed';
  endReason?: string | null;
  durationSec: number;
  /** The LY Voice bot answered; "agent" once it handed the call to a person. */
  handledBy: 'agent' | 'bot' | 'none';
  botName?: string | null;
  transferTo?: string | null;
  recording: boolean;
  /** Values the bot collected on the call. */
  collected?: Record<string, string>;
  transcript: { speaker: 'customer' | 'agent' | 'bot'; text: string; startSec?: number; language?: string }[];
  /** LY Voice's analysis, when it has arrived (usually within a minute of the call). */
  analysis?: {
    summary?: string;
    outcome?: string;
    sentiment?: { label: string; trend?: string };
    flags?: string[];
  } | null;
  transfer?: { to: string; reason?: string } | null;
  web?: { origin: string; page: string } | null;
}

export interface PresenceEntry {
  userId: string;
  name: string;
  state: 'viewing' | 'typing' | 'left';
  at: number;
}

export const renderTemplate = (body: string, params: string[]) =>
  body.replace(/\{\{(\d+)\}\}/g, (_m, n) => params[Number(n) - 1] || `{{${n}}}`);
